import React from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { MuiTextField } from '../../../../../components/MuiComponents/FormInputs';
import Button from '../../../../../components/Button';

/**
 * Authentication input section shared by the API configuration form and the IDOR
 * "Update Credentials" modal. Renders the Cookie Authentication + Custom Header
 * Authentication fields (add/remove up to 5 headers), driven by react-hook-form
 * primitives passed in from the parent (register/errors/watch/setValue + a
 * useFieldArray for `customHeaders`).
 *
 * `showAccessLevel` (default true) renders the Authenticated/Unauthenticated radio
 * above the fields — used on the config page. The Update Credentials modal passes
 * `showAccessLevel={false}` (always authenticated) so only the cookie + header
 * fields show.
 */
const AuthenticationSection = ({
  showAuthFields,
  setShowAuthFields,
  register,
  errors,
  watch,
  setValue,
  headerFields,
  appendHeader,
  removeHeader,
  replaceHeaders,
  showAccessLevel = true,
}) => {
  const currentHeaders = watch('customHeaders') || [];
  const canAddHeader = currentHeaders.length < 5;
  // When the access-level toggle is hidden, credentials are always required, so the
  // auth fields are shown unconditionally.
  const showFields = showAccessLevel ? showAuthFields : true;

  const handleAddHeader = () => {
    if (!canAddHeader) return;
    appendHeader({ headerName: '', headerValue: '' });
  };

  const handleRemoveHeader = (index) => {
    if (currentHeaders.length <= 1) {
      replaceHeaders([{ headerName: '', headerValue: '' }]);
      return;
    }
    removeHeader(index);
  };
  return (
    <div>
      {showAccessLevel && (
        <>
          <div className="flex items-center gap-2 my-2">
            <img src='/images/genpt/accesslevel.svg' alt="Access Level" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Access Level</div>
          </div>
          <div>Select the level of access for this test</div>

          <RadioGroup
            row
            value={showAuthFields ? 'authenticated' : 'unauthenticated'}
            onChange={(e) => {
              if (e.target.value === 'unauthenticated') {
                setShowAuthFields(false);
                setValue('cookieAuth', '');
                replaceHeaders([{ headerName: '', headerValue: '' }]);
              } else {
                setShowAuthFields(true);
                if (!(watch('customHeaders') || []).length) {
                  replaceHeaders([{ headerName: '', headerValue: '' }]);
                }
              }
            }}
            sx={{ alignItems: 'center', mt: 1 }}
          >
            <FormControlLabel
              value="unauthenticated"
              control={<Radio sx={{ '&.Mui-checked': { color: '#12AFB8' } }} />}
              label={<span className="text-sm text-black">UnAuthenticated</span>}
            />
            <FormControlLabel
              value="authenticated"
              control={<Radio sx={{ '&.Mui-checked': { color: '#12AFB8' } }} />}
              label={
                <span className="inline-flex items-center gap-1 text-sm text-black">
                  Authenticated
                </span>
              }
            />
          </RadioGroup>
        </>
      )}

      {/* AUTH FIELDS SHOWN BELOW */}
      {showFields && (
        <div>
          <div className="!my-4 border-t-2 border-[#EFEFEF]" />

          <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/auth_details.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Authentication Details</div>
          </div>
          <div>Enter details for authenticated access using a cookie, custom headers, or both.</div>

          <FormControl component="fieldset" className="mt-6">
          <>
                <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/cookie.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Cookie Authentication</div>
          </div>
                  <div className="text-sm text-gray-600">
                  Paste the entire cookie string used for authenticated access.
                  </div>
                  <MuiTextField
                    label=""
                    placeholder="Example: sessionid=abc123; idp_token=xyz456"
                    InputProps={{
                      ...register('cookieAuth', {
                        validate: (value) =>
                          !value || value.length <= 2000 || 'Max length is 2000 characters',
                      }),
                    }}
                    error={Boolean(errors?.cookieAuth)}
                    helperText={
                      errors?.cookieAuth?.message
                    }
                  />
                </div>

                <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/header.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Custom Header Authentication</div>
          </div>
                  <div className="text-sm text-gray-600 mt-2">
                  Add up to 5 headers to include with your requests. Click + Add New to add key–value pairs.
                  </div>

                  {headerFields.map((field, index) => (
                    <div key={field.id} className="flex flex-col gap-2 mt-2">
                      <div className="flex flex-col md:flex-row gap-3">
                        <div className="flex-1">
                          <MuiTextField
                            label=""
                            placeholder="Header Name (e.g: Authorization)"
                            InputProps={{
                              ...register(`customHeaders.${index}.headerName`, {
                                validate: (value) =>
                                  !value || value.length <= 200 || 'Max length is 200 characters',
                              }),
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <MuiTextField
                            label=""
                            placeholder="Header Value (e.g.:Basic MTIz)"
                            InputProps={{
                              ...register(`customHeaders.${index}.headerValue`, {
                                validate: (value) =>
                                  !value || value.length <= 2000 || 'Max length is 2000 characters',
                              }),
                            }}
                          />
                        </div>
                        <div className="flex items-center">
                          {currentHeaders.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveHeader(index)}
                              className="text-sm text-red-600 hover:text-red-700 font-semibold"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleAddHeader}
                      disabled={!canAddHeader}
                      className="!text-primary-600 !border-primary-200 hover:!border-primary-400 disabled:!border-gray-200 disabled:!text-gray-400"
                    >
                      + Add New
                    </Button>
                    <div className="text-grey text-sm mt-2">
                <WarningAmberIcon fontSize="inherit" color="warning" className="mr-1" />
                <span className="text-[#ED6C02] font-semibold">Please Note:</span> If the provided header or cookie values are invalid or expired, test results may be inaccurate.
              </div>
                  </div>
                </div>
              </>
          </FormControl>
        </div>
      )}
    </div>
  );
};

export default AuthenticationSection;
